import type { JobLogEntryDto } from "@shelflist/shared";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useFormat } from "@/i18n/format";
import { useErrorMessage } from "@/lib/errors";
import { fetchJobLog, POLL_INTERVAL_MS } from "@/lib/jobs";

type LevelFilter = "all" | "important" | "error";
const LEVELS: Record<LevelFilter, string | undefined> = {
  all: undefined,
  important: "warn,error",
  error: "error",
};

/** Ids of Shelflist books a log entry refers to (created, existing or duplicate). */
function bookIds(entry: JobLogEntryDto): string[] {
  const context = entry.context ?? {};
  const ids: unknown[] = [context.bookId, context.existingBookId, context.existingBookIds].flat();
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id !== ""))];
}

/**
 * The log of a job: oldest entries first, loaded 200 at a time ("Load more"), filterable by level.
 * While `live`, new entries are appended every 2 seconds once everything loaded so far is shown.
 */
export function JobLog({ jobId, live }: { jobId: string; live: boolean }) {
  const { t, i18n } = useTranslation("jobs");
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const [filter, setFilter] = useState<LevelFilter>("all");
  const [entries, setEntries] = useState<JobLogEntryDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const cursor = useRef(0);
  const busy = useRef(false);
  const generation = useRef(0);
  const hasMoreRef = useRef(false);
  const levels = LEVELS[filter];

  const load = useCallback(
    async (reset: boolean) => {
      if (busy.current && !reset) return;
      const mine = reset ? ++generation.current : generation.current;
      if (reset) {
        cursor.current = 0;
        setEntries([]);
        setLoading(true);
      }
      busy.current = true;
      try {
        const page = await fetchJobLog(jobId, cursor.current, levels);
        if (mine !== generation.current) return;
        const last = page.entries.at(-1);
        if (last) cursor.current = last.id;
        setEntries((current) => (reset ? page.entries : [...current, ...page.entries]));
        hasMoreRef.current = page.hasMore;
        setHasMore(page.hasMore);
        setError(undefined);
      } catch (caught) {
        if (mine === generation.current) setError(caught);
      } finally {
        if (mine === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    },
    [jobId, levels],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (!live) {
      // Final refresh once the job reached a terminal status.
      void load(false);
      return;
    }
    const timer = setInterval(() => {
      if (!hasMoreRef.current) void load(false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [live, load]);

  const text = (entry: JobLogEntryDto): string => {
    const context = entry.context ?? {};
    const code = typeof context.code === "string" ? context.code : undefined;
    if (!code) return entry.message;
    const reason = typeof context.reason === "string" ? `log.${code}_${context.reason}` : undefined;
    const key = [reason, `log.${code}`].find(
      (candidate) => candidate && i18n.exists(`jobs:${candidate}`),
    );
    const fields = Array.isArray(context.fields)
      ? new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(
          context.fields
            .map((field) => String(field))
            .map((field) => {
              const label = `jobs:fields.${field}`;
              return i18n.exists(label) ? t(label as "log.title") : field;
            }),
        )
      : undefined;
    return key
      ? t(key as "log.title", {
          ...(context as Record<string, never>),
          ...(fields === undefined ? {} : { fields }),
          defaultValue: entry.message,
        })
      : entry.message;
  };

  return (
    <section aria-labelledby="job-log-title" className="grid gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="job-log-title" className="text-lg font-semibold">
          {t("log.title")}
        </h2>
        <div className="grid gap-1 text-sm font-medium">
          <Label htmlFor="job-log-level">{t("log.level.label")}</Label>
          <NativeSelect
            id="job-log-level"
            className="w-56"
            value={filter}
            onChange={(event) => setFilter(event.target.value as LevelFilter)}
          >
            <option value="all">{t("log.level.all")}</option>
            <option value="important">{t("log.level.important")}</option>
            <option value="error">{t("log.level.error")}</option>
          </NativeSelect>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-destructive">
          {errorMessage(error)}
        </p>
      ) : null}
      {!loading && entries.length === 0 ? (
        <p className="text-muted-foreground rounded-lg border border-dashed px-6 py-8 text-center">
          {t("log.empty")}
        </p>
      ) : (
        <ol className="divide-y rounded-lg border text-sm" data-testid="job-log">
          {entries.map((entry) => (
            <li key={entry.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2">
              <time className="text-muted-foreground w-36 shrink-0" dateTime={entry.at}>
                {format.dateTime(entry.at)}
              </time>
              <Badge
                variant={
                  entry.level === "error"
                    ? "destructive"
                    : entry.level === "warn"
                      ? "default"
                      : "outline"
                }
                className="w-20 shrink-0"
              >
                {t(`log.levels.${entry.level}`)}
              </Badge>
              <span className="min-w-0 flex-1 break-words">
                {text(entry)}
                {typeof entry.context?.calibreId === "number" ? (
                  <span className="text-muted-foreground ml-2">
                    ({t("log.bookRef", { calibreId: entry.context.calibreId })})
                  </span>
                ) : null}
                {bookIds(entry).map((id) => (
                  <Link
                    key={id}
                    to="/books/$id"
                    params={{ id }}
                    className="ml-2 underline underline-offset-4"
                  >
                    {t("log.openBook")}
                  </Link>
                ))}
              </span>
            </li>
          ))}
        </ol>
      )}
      {hasMore ? (
        <Button variant="outline" onClick={() => void load(false)} className="justify-self-start">
          {t("log.loadMore")}
        </Button>
      ) : null}
    </section>
  );
}
